import * as React from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "motion/react";
import { RiAddLine, RiArrowDownSLine, RiArrowUpSLine } from "@remixicon/react";
import { IconButton } from "~/components/ui/icon-button";
import { SectionTitle } from "~/components/ui/section-title";
import { HighlightsMobileCard } from "~/components/products/HighlightsMobileCard";
import { cn } from "~/lib/utils";
import type { ProductHighlightsContent } from "~/data/products/types";

interface HighlightsCarouselProps {
  content: ProductHighlightsContent;
}

// Same autoplay contract as HighlightsAccordion (version A): starts once the
// section scrolls into view, advances on this interval, and is permanently
// disabled the moment the user interacts with a nav button, item, or dot.
const AUTO_ADVANCE_SECONDS = 5;
// Plain CSS height transition, not Motion's `layout` FLIP — matches
// HighlightsAccordion's own technique. A `layout`-animated card here produced
// an overlap glitch: Motion animates the card's box via a transform-scale
// illusion while the real DOM (and the description, which only fades in) is
// already laid out at full size, so the description rendered on top of the
// still-"shrunk" title before the transform caught up.
//
// Each item's expanded slot is sized to its own description
// (item.descriptionLines, default 2) instead of one fixed height for all 5.
const DESCRIPTION_LINE_HEIGHT = 20;
const DEFAULT_DESCRIPTION_LINES = 2;

const COLLAPSED_PILL_HEIGHT = 36 + 24;
const OPEN_CARD_CHROME_HEIGHT = 48 + 20 + 8;
const ITEM_GAP = 12;

// Version B of the "Discover what Bouncie Drive can do." section (Figma node
// 7981:61467) — a card carousel: one expanded item plus collapsed pills, stepped
// through via up/down nav buttons, item clicks, or the pagination dots below the
// list. See HighlightsAccordion for the version A (inline-expand) treatment this
// is being compared against.
export function HighlightsCarousel({ content }: HighlightsCarouselProps) {
  const { t } = useTranslation("products");
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [userHasInteracted, setUserHasInteracted] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const displayIndex = activeIndex ?? (isInView ? 0 : null);

  const descriptionSlotHeights = content.items.map(
    (item) => (item.descriptionLines ?? DEFAULT_DESCRIPTION_LINES) * DESCRIPTION_LINE_HEIGHT,
  );
  const maxDescriptionSlotHeight = Math.max(...descriptionSlotHeights);
  const itemsListHeight =
    (content.items.length - 1) * COLLAPSED_PILL_HEIGHT +
    OPEN_CARD_CHROME_HEIGHT +
    maxDescriptionSlotHeight +
    (content.items.length - 1) * ITEM_GAP;

  React.useEffect(() => {
    if (!isInView || userHasInteracted) return;
    const interval = setInterval(() => {
      setActiveIndex((current) => ((current ?? 0) + 1) % content.items.length);
    }, AUTO_ADVANCE_SECONDS * 1000);
    return () => clearInterval(interval);
  }, [isInView, userHasInteracted, content.items.length]);

  function goTo(index: number) {
    const count = content.items.length;
    setActiveIndex(((index % count) + count) % count);
    setUserHasInteracted(true);
  }

  return (
    <section ref={sectionRef} id="features" className="scroll-mt-20 bg-white py-15">
      <div className="wrap flex flex-col gap-14">
        <div className="flex w-full flex-col items-center gap-2 lg:items-start lg:gap-4">
          <div className="flex flex-col items-center gap-2 lg:items-start lg:gap-3">
            <p className="eyebrow">
              {t(content.eyebrow)}
            </p>
            <SectionTitle className="text-text-heading max-w-[255px] text-center lg:max-w-none lg:text-left lg:whitespace-nowrap">
              {t(content.heading)}
            </SectionTitle>
          </div>
          <p className="text-text-body-alt lg:text-md max-w-[269px] text-center text-sm lg:max-w-[714px] lg:text-left">
            {t(content.description)}
          </p>
        </div>

        <div className="lg:hidden">
          <HighlightsMobileCard items={content.items} />
        </div>

        <div className="hidden lg:flex lg:flex-row lg:items-stretch lg:justify-between lg:gap-14">
          <div className="flex w-full flex-col lg:mt-8 lg:max-w-[550px]">
            {/* Nav buttons center against just this row (the cards), not the dots
                below — centering against the cards+dots combined made the buttons
                sit visibly low, since the dots row adds height below the cards with
                nothing matching it above. */}
            <div className="flex w-full items-stretch gap-3">
              <div className="flex shrink-0 flex-col items-center justify-center gap-10">
                {/* Figma (node 8192:193572): gray fill with a light ring + soft shadow,
                    not a flat white fill. */}
                <IconButton
                  icon={RiArrowUpSLine}
                  variant="primary"
                  className="border-neutral-100 bg-neutral-50 shadow-sm"
                  onClick={() => goTo((displayIndex ?? 0) - 1)}
                  aria-label={t("drive.highlightsB.navPrevious")}
                />
                <IconButton
                  icon={RiArrowDownSLine}
                  variant="primary"
                  className="border-neutral-100 bg-neutral-50 shadow-sm"
                  onClick={() => goTo((displayIndex ?? 0) + 1)}
                  aria-label={t("drive.highlightsB.navNext")}
                />
              </div>

              <div className="flex w-full flex-1 flex-col gap-3" style={{ height: itemsListHeight }}>
                {content.items.map((item, index) => {
                  const isActive = index === displayIndex;
                  return (
                    <button
                      key={index}
                      data-sid={`highlights-item:${index}`}
                      type="button"
                      onClick={() => goTo(index)}
                      className={cn(
                        "flex rounded-2xl text-left transition-[background-color,border-radius,padding] duration-300",
                        isActive
                          ? "items-start rounded-[28px] bg-bouncie-100/80 p-6"
                          : "w-fit items-center gap-3 self-start bg-bouncie-100/35 py-4.5 pr-8 pl-3.5",
                      )}
                    >
                      {/* Collapses to nothing (width/height/opacity) instead of just
                          disappearing, so the title doesn't jump sideways when the icon
                          goes away. */}
                      <div
                        className={cn(
                          "flex shrink-0 items-center justify-center overflow-hidden transition-[width,height,opacity] duration-300",
                          isActive ? "h-0 w-0 opacity-0" : "h-6 w-6 opacity-100",
                        )}
                      >
                        <RiAddLine className="text-text-heading size-6 shrink-0" />
                      </div>
                      {/* w-full and the title/description gap only apply when active —
                          w-full is what stretched the whole collapsed pill full-width
                          before (a w-full child forces the outer w-fit button to expand
                          to the row's full available width). The gap mattered similarly:
                          flexbox still reserves it between the title and the description
                          wrapper even while that wrapper is collapsed to 0 height, which
                          was nudging the title up out of vertical center with the icon. */}
                      <div className={cn("flex flex-col", isActive && "w-full gap-2")}>
                        {/* text-[18px] + leading-[1.1] matches Figma's "Body/L" title
                            spec (18px/1.1) for both states — was text-base (16px, 1.5
                            default leading) before, which was also what put the expanded
                            card's total height over Figma's 116px. Deliberately not
                            `text-lg`: this project's theme redefines that to 20px, not
                            Tailwind's default (and Figma's) 18px. */}
                        <span
                          className={cn(
                            "font-display text-text-heading text-[18px] leading-[1.1]",
                            isActive ? "font-bold" : "font-normal",
                          )}
                        >
                          {t(item.title)}
                        </span>
                        {/* Width collapses to 0 when inactive — otherwise this text
                            (fully hidden, but with no width constraint) still lays out on
                            one long max-content line and forces the collapsed pill's
                            fit-content width to match it, which is what was stretching
                            every "closed" pill to the full column width regardless of its
                            much shorter title. */}
                        <div
                          className="overflow-hidden transition-[height] duration-300 ease-out"
                          style={{ height: isActive ? descriptionSlotHeights[index] : 0, width: isActive ? "100%" : 0 }}
                        >
                          {/* Delay tuned so the text only fades in once the card above it
                              has essentially finished expanding, per feedback that it was
                              appearing mid-expansion. */}
                          <span
                            className="text-text-heading block text-sm leading-5 transition-opacity duration-150"
                            style={{ opacity: isActive ? 1 : 0, transitionDelay: isActive ? "220ms" : "0ms" }}
                          >
                            {t(item.description)}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dots sit in their own row below, offset by a spacer matching the nav
                column's width so they stay left-aligned with the cards above (not
                the nav buttons) — same reason the nav buttons live outside this row:
                keeping the dots out of the nav's centering calculation. */}
            <div className="flex w-full gap-3">
              <div className="hidden w-12 shrink-0 lg:block" />
              <div className="mt-2 flex w-full flex-1 items-center justify-start gap-2 py-6">
                {content.items.map((item, index) => {
                  const isActive = index === displayIndex;
                  return (
                    <button
                      key={index}
                      data-sid={`highlights-dot:${index}`}
                      type="button"
                      aria-label={t(item.title)}
                      aria-current={isActive}
                      onClick={() => goTo(index)}
                      className={cn(
                        "relative h-2 overflow-hidden rounded-full bg-neutral-100 transition-[width] duration-300",
                        isActive ? "w-12" : "w-2",
                      )}
                    >
                      {/* Fill track — only the active dot ever animates: while autoplay
                          is still running it fills empty-to-full over AUTO_ADVANCE_SECONDS,
                          in sync with the interval that advances activeIndex, and lands on
                          full right as it hands off to the next item. Every other dot
                          (including the one that just lost "active") snaps to empty
                          instantly (transition: "none") instead of animating — otherwise
                          the previous dot would visibly drain back down over the next
                          item's whole timer window. Same trick as HighlightsAccordion's
                          timer bar, just width-driven instead of height-driven. */}
                      <span
                        className="bg-bouncie-900 absolute inset-y-0 left-0 rounded-full"
                        style={{
                          width: isActive ? "100%" : "0%",
                          transition: !isActive
                            ? "none"
                            : userHasInteracted
                              ? "width 0.3s ease"
                              : `width linear ${AUTO_ADVANCE_SECONDS}s`,
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* lg:self-center — its fixed h-[380px] is shorter than the row once an item
              description grows the text column, and items-stretch above anchors
              anything with a definite height to the row's top instead of stretching it. */}
          <div className="relative hidden w-full shrink-0 lg:block lg:h-[380px] lg:w-[493px] lg:self-center">
            <div className="flex aspect-[709/447] w-full items-center justify-center rounded-2xl border border-dashed border-neutral-200 bg-white/60 lg:aspect-auto lg:h-full">
              <div
                className="flex aspect-[85/44] w-[85%] items-center justify-center rounded-full"
                style={{ backgroundColor: "var(--neutral-80)" }}
              >
                <span className="font-display text-2xl font-extrabold tracking-wide text-[#e0219b]">FPO</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
